import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ReactSVG } from 'react-svg';
import { TFunction } from 'i18next';
import {
  Button,
  PaidStatusPill,
  calculateInvoiceFees,
  fDateTime,
  fToCurrency,
  useParameters,
} from '@web/utils';
import arrowTop from '@web/utils/src/assets/icons/functions/arrow-top.svg';
import { InvoiceStatuses } from '@web/utils/src/components/InvoiceStatus/InvoiceStatus';
import {
  StyledCheckbox,
  StyledPagination,
  StyledTable,
} from '@/commons/ui/theme/materialStyles';
import { TableHeader } from '@/components/TableHeader';
import { TableNotFound } from '@/components/TableNotFound';
import { useCustomToast } from '@/hooks/useCustomToast';
import { Invoice } from '@/types';
import { useCreatePaymentForInvoiceMutation } from '../../SingleInvoice/invoice.generated';
import { TableBody, TableCell, TableRow } from '@mui/material';

// ----------------------------------------------------------------------

type TableKey = keyof Invoice;

const TABLE_HEAD = (
  t: TFunction,
): {
  id: TableKey;
  label: string;
  alignRight: boolean;
}[] => [
  {
    id: 'id',
    label: t('invoices.table_columns.invoice_number'),
    alignRight: false,
  },
  {
    id: 'totalGross',
    label: t('invoices.table_columns.total_gross'),

    alignRight: false,
  },
  {
    id: 'issuedAt',
    label: t('invoices.table_columns.invoice_date'),

    alignRight: false,
  },
  {
    id: 'status',
    label: t('invoices.table_columns.invoice_status'),
    alignRight: false,
  },
  {
    id: 'documentID', // mollie pay later
    label: 'mollie-logo',
    alignRight: false,
  },
];

// ----------------------------------------------------------------------

type PInvoice = Pick<
  Invoice,
  'id' | 'paymentOptions' | 'totalGross' | 'issuedAt'
> & {
  status: InvoiceStatuses;
};

interface InvoicesTableProps {
  invoiceList: PInvoice[];
}

interface InvoiceParams {
  retailerId: string;
}

const InvoicesTable: React.FC<InvoicesTableProps> = (props) => {
  const { invoiceList } = props;
  const { retailerId } = useParameters<InvoiceParams>();
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(20);
  const [selected, setSelected] = useState<string[]>([]);
  const navigate = useNavigate();

  const handleSelect = (_event, id: string) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected: string[] = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }
    setSelected(newSelected);
  };

  const handlePageChange = (e, newPage) => {
    if (newPage > page) {
      setPage((val) => val + 1);
    } else {
      setPage((val) => val - 1);
    }
  };

  const handleRowsPerPageChange = (e) => {
    setPerPage(e.target.value);
  };

  const handleRowClick = (invoice: PInvoice) => {
    navigate(`/documents/${retailerId}/invoices/${invoice.id}`);
  };

  // create variable to display invoices base don page and perPage
  const invoices = useMemo(() => {
    const start = page * perPage;
    const end = start + perPage;
    return invoiceList ? invoiceList.slice(start, end) : [];
  }, [invoiceList, page, perPage]);

  const isNotFound = !invoices.length && !!invoices;

  const [_, createPaymentInvoice] = useCreatePaymentForInvoiceMutation();
  const toast = useCustomToast();

  const handleCreateInvoicePayment = async (
    invoiceId: string,
    paymentOptionKey: string,
  ) => {
    try {
      const response = await createPaymentInvoice({
        invoiceId: invoiceId,
        paymentOptionKey: paymentOptionKey,
      });

      if (response.data?.createPaymentForInvoice?.checkoutUrl) {
        window.open(
          response.data?.createPaymentForInvoice?.checkoutUrl,
          '_blank',
        );
      } else {
        toast('error', t('invoices.error_creating_invoice'));
      }
    } catch (error) {
      console.log(error);
      toast('error', t('invoices.error_creating_invoice'));
    }
  };

  return (
    <>
      <StyledTable stickyHeader>
        <TableHeader
          headLabel={TABLE_HEAD(t)}
          rowCount={invoices.length}
          numSelected={selected.length}
        />
        <TableBody>
          {invoices.map((invoice) => {
            const selectedInvoice = selected.indexOf(invoice.id) !== -1;

            return (
              <TableRow
                hover
                key={invoice.id}
                tabIndex={-1}
                role="checkbox"
                selected={selectedInvoice}
                className="last:bg-border-0 relative"
              >
                <TableCell padding="checkbox">
                  <StyledCheckbox
                    checked={selectedInvoice}
                    onChange={(event) => handleSelect(event, invoice.id)}
                  />
                </TableCell>

                <TableCell align="left">
                  <span className="table-cell text-1-4 font-li">
                    {invoice.id}
                  </span>
                </TableCell>
                <TableCell align="left">
                  <span className="table-cell text-1-4 font-li">
                    {fToCurrency(invoice.totalGross)}
                  </span>
                </TableCell>
                <TableCell align="left">
                  <span className="table-cell text-1-4 font-li">
                    {fDateTime(invoice.issuedAt)}
                  </span>
                </TableCell>
                <TableCell align="left">
                  <PaidStatusPill status={invoice.status} t={t} />
                </TableCell>
                <TableCell align="left">
                  {invoice.status !== 'PAID' &&
                    invoice.paymentOptions.length > 0 && (
                      <div className="flex w-full justify-between ">
                        <div className="text-1-4">
                          <ul className="list-disc">
                            <li className="leading-1-63">
                              {t('invoices.pay_in', {
                                value: '120',
                              })}
                            </li>
                            <li className="leading-1-63">
                              {t('invoices.pay_additional', {
                                value: calculateInvoiceFees(
                                  invoice.totalGross || 0,
                                  invoice.paymentOptions[0].fee,
                                ),
                              })}
                            </li>
                          </ul>
                        </div>
                        <div className="pr-4-0">
                          <Button
                            onClick={() =>
                              handleCreateInvoicePayment(
                                invoice.id,
                                invoice.paymentOptions[0].id,
                              )
                            }
                          >
                            {t('invoices.pay_later')}
                          </Button>
                        </div>
                      </div>
                    )}
                </TableCell>
                <div
                  className="absolute right-0 h-full flex items-center px-1-0 cursor-pointer opacity-70 hover:opacity-90"
                  onClick={() => handleRowClick(invoice)}
                >
                  <ReactSVG src={arrowTop} className="text-white rotate-90" />
                </div>
              </TableRow>
            );
          })}
        </TableBody>

        {isNotFound && <TableNotFound />}
      </StyledTable>
      <StyledPagination
        count={invoiceList.length}
        page={page}
        rowsPerPageOptions={[20, 50, 100]}
        rowsPerPage={perPage}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
      />
    </>
  );
};

export default InvoicesTable;
